import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import { environment } from './environments/environment';
import { GlobalErrorHandlerService } from '@shared/services/global-error-handler.service';
import { initAuth, initFeatureToggles, setupLocalisation } from '@src/app/app-initializer.factory';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from './app/features/authentication/services/auth.service';
import { SessionService } from './app/features/authentication/services/session.service';
import { FeatureToggleService } from '@core/feature-toggle/feature-toggle.service';
import { RouteStyleManager } from '@core/style-manager/route-style-manager';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaginatorI18nService } from '@shared/services/localisation/paginator-i18n.service';
import { DATE_ADAPTER_PROVIDERS } from '@utils/date.utils';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app/app-routing.module';
import { AuthenticationModule } from './app/features/authentication/authentication.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { CoreModule } from '@core/core.module';
import { DashboardModule } from './app/features/dashboard/dashboard.module';
import { EducationalResourcesModule } from './app/features/educational-resources/educational-resources.module';
import { ErrorHandlingModule } from '@shared/components/error-handling/error-handling.module';
import { FormModule } from '@shared/components/form/form.module';
import { MediaModule } from './app/features/media/media.module';
import { provideNgxMask } from 'ngx-mask';
import { OrthoModule } from './app/features/ortho/ortho.module';
import { OrthognathicModule } from './app/features/orthognathic/orthognathic.module';
import { PartnerModule } from './app/features/partner/partner.module';
import { PatientFormsContainerModule } from '@shared/components/patient-forms-container/patient-forms-container.module';
import { PatientModule } from './app/features/patient/patient.module';
import { PractitionerModule } from './app/features/practitioner/practitioner.module';
import { ReactiveFormsModule } from '@angular/forms';
import { ReportsModule } from './app/features/reports/reports.module';
import { ReviewModule } from './app/features/review/review.module';
import { SpeechModule } from './app/features/speech/speech.module';
import { SuspiciousRecordsReviewModule } from './app/features/suspicious-records/suspicious-records.module';
import { TreatmentCenterModule } from './app/features/treatment-center/treatment-center.module';
import { FooterComponentModule } from '@shared/components/footer/footer.component';
import { BidiModule } from '@angular/cdk/bidi';
import { AppComponent } from './app/app.component';
import { initSentryIfApplicable } from './app/config/sentry.config';
import { StxTranslateLoader } from '@core/i18n/stx-translate-loader';

export function createTranslateLoader(httpClient: HttpClient) {
  return new StxTranslateLoader(httpClient);
}

initSentryIfApplicable();

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(
      AppRoutingModule,
      AuthenticationModule,
      BrowserModule,
      CoreModule,
      DashboardModule,
      EducationalResourcesModule,
      ErrorHandlingModule,
      FormModule,
      MediaModule,
      OrthoModule,
      OrthognathicModule,
      PartnerModule,
      PatientFormsContainerModule,
      PatientModule,
      PractitionerModule,
      ReactiveFormsModule,
      ReportsModule,
      ReviewModule,
      SpeechModule,
      SuspiciousRecordsReviewModule,
      TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useFactory: createTranslateLoader, deps: [HttpClient] }
      }),
      TreatmentCenterModule,
      FooterComponentModule,
      BidiModule
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideAppInitializer(() => {
      const initializerFn = initAuth(inject(AuthService), inject(TranslateService), inject(Injector));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      inject(SessionService);
    }),
    provideAppInitializer(() => {
      return setupLocalisation(inject(TranslateService), inject(Injector));
    }),
    provideAppInitializer(() => {
      const initializerFn = initFeatureToggles(inject(FeatureToggleService));
      return initializerFn();
    }),
    RouteStyleManager,
    {
      provide: MatPaginatorIntl,
      deps: [PaginatorI18nService],
      useFactory: (paginatorI18nService: PaginatorI18nService) => paginatorI18nService.getPaginatorIntl()
    },
    ...DATE_ADAPTER_PROVIDERS,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNgxMask()
  ]
}).catch(err => {
  alert(
    'Smile Train Express has encountered an unexpected problem. Please refresh page. If the problem persists please contact STX Admin.'
  );
  console.error(err);
});
